/**
 * ExamReferralSection
 * Displays uncovered exams and allows assigning referral clinics
 */

import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material';
import { DataTable, type DataTableColumn, PrerequisiteBlock } from '../../../components';
import appointmentExamService from '../../../services/appointmentExamService';

interface AlternativeClinic {
  clinicId: number;
  clinicName: string;
  price: number | null;
  turnaroundDays: number | null;
}

interface UncoveredExam {
  examId: number;
  examName: string;
}

interface ExamReferralSectionProps {
  unsupportedExams: UncoveredExam[];
  companyId: number;
  mainClinicId: number;
  mainClinicName: string;
  referralAssignments: Map<number, number>;
  onAssignmentChange: (assignments: Map<number, number>) => void;
}

export const ExamReferralSection: React.FC<ExamReferralSectionProps> = ({
  unsupportedExams,
  companyId,
  mainClinicId,
  mainClinicName,
  referralAssignments,
  onAssignmentChange,
}) => {
  const [alternativeClinics, setAlternativeClinics] = useState<Record<number, AlternativeClinic[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (unsupportedExams.length === 0 || !mainClinicId || !companyId) return;

    const examIds = unsupportedExams.map((e) => e.examId);

    setLoading(true);
    appointmentExamService
      .checkClinicCoverage(mainClinicId, examIds, companyId)
      .then((result) => {
        setAlternativeClinics(result.alternativeClinics);
      })
      .catch((err) => {
        console.error('Erro ao verificar cobertura:', err);
      })
      .finally(() => setLoading(false));
  }, [unsupportedExams, mainClinicId, companyId]);

  if (unsupportedExams.length === 0) return null;

  const handleClinicChange = (examId: number, clinicId: number) => {
    const updated = new Map(referralAssignments);
    if (clinicId === 0) {
      updated.delete(examId);
    } else {
      updated.set(examId, clinicId);
    }
    onAssignmentChange(updated);
  };

  // Só entram no select os exames com alguma clínica alternativa; sem alternativa, o select
  // nunca teria opção a oferecer — o bloco acima da tabela avisa e indica o que fazer.
  const examesComAlternativa = unsupportedExams.filter((e) => (alternativeClinics[e.examId] || []).length > 0);
  const examesSemAlternativa = unsupportedExams.filter((e) => (alternativeClinics[e.examId] || []).length === 0);

  // Sem exame atribuível, [].every() daria true por vacuidade e a severidade viraria "info" —
  // errado, pois a seção continua bloqueada pelo PrerequisiteBlock.
  const allAssigned =
    examesComAlternativa.length > 0 && examesComAlternativa.every((e) => referralAssignments.has(e.examId));

  const columns: DataTableColumn<UncoveredExam>[] = [
    {
      id: 'examName',
      label: 'Exame',
      primary: true,
      render: (exam) => exam.examName,
    },
    {
      id: 'referralClinic',
      label: 'Clínica de Encaminhamento',
      render: (exam) => {
        const clinics = alternativeClinics[exam.examId] || [];
        return (
          <FormControl size="small" fullWidth sx={{ minWidth: 200 }}>
            <InputLabel>Selecione...</InputLabel>
            <Select
              value={referralAssignments.get(exam.examId) || ''}
              onChange={(e) => handleClinicChange(exam.examId, Number(e.target.value))}
              label="Selecione..."
            >
              <MenuItem value="">
                <em>Selecione...</em>
              </MenuItem>
              {clinics.map((c) => (
                <MenuItem key={c.clinicId} value={c.clinicId}>
                  {c.clinicName}
                  {c.price != null && ` - R$ ${Number(c.price).toFixed(2)}`}
                  {c.turnaroundDays != null && ` (${c.turnaroundDays} dias)`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
  ];

  // Com exame sem alternativa, o PrerequisiteBlock abaixo já é o aviso (é um Alert em si) —
  // envolvê-lo em outro Alert empilharia duas faixas coloridas para o mesmo bloqueio.
  const semAlternativa = examesSemAlternativa.length > 0;
  const Wrapper = semAlternativa ? Box : Alert;
  const wrapperProps = semAlternativa
    ? { sx: { mt: 2 } }
    : { severity: allAssigned ? ('info' as const) : ('warning' as const), sx: { mt: 2 }, icon: false };

  return (
    <Wrapper {...wrapperProps}>
      <Typography variant="subtitle2" gutterBottom>
        Exames que requerem encaminhamento
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        A clínica <strong>{mainClinicName}</strong> não realiza os seguintes exames.
        {examesComAlternativa.length > 0 && ' Selecione uma clínica para encaminhamento:'}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {examesSemAlternativa.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <PrerequisiteBlock
                title={`Nenhuma clínica realiza: ${examesSemAlternativa.map((e) => e.examName).join(', ')}`}
                reason="Sem clínica de encaminhamento, estes exames não podem ser incluídos no agendamento."
                resolve={{
                  label: 'Vincular exame a uma clínica',
                  permission: 'provider_exam.update',
                  mode: 'navigate',
                  route: '/providers',
                }}
                deniedHint="Peça a um administrador para vincular estes exames a alguma clínica."
              />
            </Box>
          )}

          {examesComAlternativa.length > 0 && (
            <DataTable
              columns={columns}
              rows={examesComAlternativa}
              getRowId={(exam) => exam.examId}
              emptyState={{
                title: 'Nenhum exame para encaminhar',
                description:
                  'Esta lista traz os exames escolhidos neste agendamento que a clínica principal não realiza.',
              }}
            />
          )}
        </>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Os encaminhamentos serão enviados por e-mail após a criação do agendamento.
      </Typography>
    </Wrapper>
  );
};

export default ExamReferralSection;
